'use client';

import React from 'react';

import { Body, Heading } from '@depop/web-thrift-ui/Typography';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';

import { Features } from './components/Features';
import { Hero } from './components/Hero';
import { Questions } from './components/Questions';
import { RedemptionButton } from './components/RedemptionButton';
import styles from './styles.module.css';

import { getPartnerDisplay } from '@/modules/partners/constants.ts';

type Props = {
  partner: string;
  redemptionLink: string;
};

const cloudfrontHost = process.env.NEXT_PUBLIC_DEPOP_CLOUDFRONT_HOST;

export function Partner({ partner, redemptionLink }: Props) {
  const t = useTranslations('partners');

  const { name, hero, intro, useTo, features, questions, tAndC, cta } =
    getPartnerDisplay(partner);

  return (
    // eslint-disable-next-line no-restricted-syntax
    <div data-testid="partner-page" data-redemption-link={redemptionLink}>
      <Hero
        hero={hero}
        cloudfrontHost={cloudfrontHost}
        name={name}
        redemptionLink={redemptionLink}
      />
      {intro && (
        <section
          className={clsx(styles.section, styles.introContainer)}
          // eslint-disable-next-line no-restricted-syntax
          data-testid="intro-section"
        >
          <div className={styles.logoContainer}>
            {intro.images.map((image, index) => (
              <img
                key={index}
                className={styles.displayImage}
                src={`${cloudfrontHost}/web/assets/partners/${image}`}
                srcSet={`${cloudfrontHost}/web/assets/partners/${image}`}
                // eslint-disable-next-line no-restricted-syntax
                data-testid="intro-image"
                alt=""
              />
            ))}
          </div>
          {/* eslint-disable-next-line no-restricted-syntax */}
          <Body variant="sm-regular" data-testid="intro-copy">
            {t(intro.copy)}
          </Body>
        </section>
      )}
      <Features
        features={features}
        name={name}
        redemptionLink={redemptionLink}
        cloudfrontHost={cloudfrontHost}
      />
      {useTo && (
        // eslint-disable-next-line no-restricted-syntax
        <section className={styles.pinkBackground} data-testid="use-to-section">
          <div className={styles.section}>
            <Heading
              variant="lg"
              className={styles.sectionTitle}
              // eslint-disable-next-line no-restricted-syntax
              data-testid="use-to-title"
            >
              {t(`Partners.${name}.TitleUseTo`)}
            </Heading>
            <div className={styles.columns}>
              {useTo.reasons.map((reason, index) => (
                <div key={index}>
                  <img
                    className={styles.displayImage}
                    src={`${cloudfrontHost}/web/assets/partners/${reason.image}`}
                    srcSet={`${cloudfrontHost}/web/assets/partners/${reason.image}`}
                    // eslint-disable-next-line no-restricted-syntax
                    data-testid="use-to-image"
                    alt=""
                  />
                  {/* eslint-disable-next-line no-restricted-syntax */}
                  <Heading variant="sm" data-testid={`use-to-copy-${index}`}>
                    {t(reason.copy)}
                  </Heading>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
      <Questions questions={questions} name={name} />
      {tAndC && (
        // eslint-disable-next-line no-restricted-syntax
        <section className={styles.section} data-testid="t-and-c-section">
          <Heading
            variant="lg"
            className={styles.sectionTitle}
            // eslint-disable-next-line no-restricted-syntax
            data-testid="t-and-c-title"
          >
            {t(`Partners.${name}.TandCTitle`)}
          </Heading>
          {/* eslint-disable-next-line no-restricted-syntax */}
          <Body variant="sm-regular" data-testid="t-and-c-desc">
            {t(`Partners.${name}.TandCDesc`)}
          </Body>
        </section>
      )}
      {cta && (
        // eslint-disable-next-line no-restricted-syntax
        <footer className={styles.cta} data-testid="cta-section">
          <div className={styles.ctaContentWrapper}>
            <Heading variant="lg">{t(`Partners.${name}.CtaTitle`)}</Heading>
            <RedemptionButton
              ctaText={t(`Partners.${name}.ButtonCta`)}
              redemptionLink={redemptionLink}
            />
          </div>
        </footer>
      )}
    </div>
  );
}
